import { useMemo, useState } from "react";
import { Database, Play, Table2, RefreshCw, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

export default function AzureSql() {
  const { state } = useAzure();
  const resources = state?.resources || [];
  const servers = resources.filter((r) => r.type === "Microsoft.Sql/servers");
  const dbs = resources.filter((r) => r.type === "Microsoft.Sql/servers/databases");

  const [sel, setSel] = useState(null); // {server, database}
  const [tables, setTables] = useState([]);
  const [sql, setSql] = useState("SELECT TOP 100 * FROM ");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [readonly, setReadonly] = useState(false);

  const openDb = async (server, database) => {
    setSel({ server, database });
    setResult(null);
    setSql(`SELECT * FROM `);
    try {
      const { data } = await api.post("/azure/sql/tables", { server, database });
      setTables(data.tables || []);
    } catch (e) { setTables([]); }
  };

  const runQuery = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const { data } = await api.post("/azure/sql/query", { ...sel, sql, readonly });
      setResult(data);
    } catch (e) { setResult({ error: "request_failed", message: azErr(e) }); } finally { setBusy(false); }
  };

  const serverOf = (dbName) => dbName.split("/")[0];
  const nameOf = (dbName) => dbName.split("/")[1];

  return (
    <div data-testid="az-sql">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Database className="h-5 w-5 text-[#0078d4]" />
        <h2 className="font-semibold">Azure SQL — servers &amp; databases</h2>
        <span className="text-xs text-muted-foreground ml-2">Backed by a real SQL engine · your queries actually execute</span>
      </div>

      {!sel ? (
        <div className="space-y-5">
          {servers.map((s) => (
            <div key={s.id} className="border border-border rounded-md overflow-hidden" data-testid={`az-sql-server-${s.name}`}>
              <div className="px-4 py-2 bg-muted/50 text-sm font-medium flex items-center gap-2">
                <Database className="h-4 w-4 text-[#0078d4]" /> {s.name}
                <span className="text-xs text-muted-foreground font-normal">{s.properties.fqdn}</span>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Database</TableHead><TableHead>Tier</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {dbs.filter((d) => serverOf(d.name) === s.name).map((d) => (
                    <TableRow key={d.id} data-testid={`az-sql-db-${nameOf(d.name)}`}>
                      <TableCell className="font-medium text-[#0078d4]">{nameOf(d.name)}</TableCell>
                      <TableCell>{d.properties.sku}</TableCell>
                      <TableCell className="text-xs text-emerald-600">{d.properties.status}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`az-sql-open-${nameOf(d.name)}`} onClick={() => openDb(s.name, nameOf(d.name))}>
                          <Play className="h-3.5 w-3.5" /> Query editor
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="ghost" onClick={() => setSel(null)}>← Databases</Button>
            <span className="text-sm font-medium">{sel.server} / <span className="text-[#0078d4]">{sel.database}</span></span>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={readonly} onChange={(e) => setReadonly(e.target.checked)} data-testid="az-sql-readonly" />
              <ShieldCheck className="h-3.5 w-3.5" /> db_datareader (read-only) role
            </label>
          </div>

          <div className="grid grid-cols-[200px_1fr] gap-4">
            <div className="border border-border rounded-md p-2">
              <div className="text-xs font-medium text-muted-foreground px-1 pb-1 flex items-center gap-1"><Table2 className="h-3.5 w-3.5" /> Tables</div>
              {tables.map((t) => (
                <button key={t.table} onClick={() => setSql(`SELECT * FROM ${t.table}`)} data-testid={`az-sql-table-${t.table}`}
                  className="w-full text-left px-2 py-1 rounded text-sm hover:bg-accent flex justify-between">
                  <span>{t.table}</span><span className="text-xs text-muted-foreground">{t.rows}</span>
                </button>
              ))}
              {tables.length === 0 && <div className="text-xs text-muted-foreground px-1 py-2">No tables.</div>}
            </div>

            <div>
              <textarea value={sql} onChange={(e) => setSql(e.target.value)} data-testid="az-sql-editor" spellCheck={false}
                className="w-full h-32 font-mono text-sm p-3 rounded-md border border-border bg-[#0b0b0c] text-emerald-200/90 outline-none" />
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" className="gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={runQuery} disabled={busy} data-testid="az-sql-run">
                  <Play className="h-3.5 w-3.5" /> {busy ? "Running…" : "Run"}
                </Button>
                <span className="text-xs text-muted-foreground">Supports SELECT / INSERT / UPDATE / DELETE / CREATE / JOIN / VIEW / INDEX</span>
              </div>

              {result && (
                <div className="mt-3" data-testid="az-sql-result">
                  {result.error ? (
                    <div className="rounded-md bg-red-500/10 text-red-600 px-3 py-2 text-sm font-mono">{result.message || result.error}</div>
                  ) : result.columns?.length ? (
                    <div className="border border-border rounded-md overflow-auto max-h-[45vh]">
                      <Table>
                        <TableHeader><TableRow>{result.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
                        <TableBody>
                          {result.rows.map((row, i) => (
                            <TableRow key={i}>{row.map((cell, j) => <TableCell key={j} className="font-mono text-xs">{cell === null ? "NULL" : String(cell)}</TableCell>)}</TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-2 text-sm">{result.message}</div>
                  )}
                  {result.message && !result.error && <div className="text-xs text-muted-foreground mt-1">{result.message}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
